import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faFolderOpen,
  faSlidersH,
  faSterlingSign,
  faUser,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import CustomInput from "../components/customs/custom-input";
import CustomRadioButton from "../components/customs/custom-radio-button";
import CustomSelect from "../components/customs/custom-select";
import CustomButton from "../components/customs/custom-button";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import Loader from "../components/loader";
import { useChaseEditModel } from "../store/use-chase-edit-model";
import ModalPortal from "../components/modal-portal";
import RenewalDocuments from "../components/renewal-documents";
import {
  BRAND_OPTIONS,
  POLICY_SOURCE_OPTIONS,
  COVER_TYPE_OPTIONS,
  LAPSE_REASONS,
  toChasePayload,
  toEditForm,
  validateEditForm,
  type EditChaseForm,
} from "../utils/chase-edit.constants";

interface IEditChaseModel {
  UpdateItemData: () => void;
}

const EditChaseModel: React.FC<IEditChaseModel> = ({ UpdateItemData }) => {
  const { initialValues, onClose } = useChaseEditModel();
  const [loading, setLoading] = useState(false);

  // One state object drives the whole form; `set` shallow-merges a patch.
  const [value, setValue] = useState<EditChaseForm>(() =>
    toEditForm(initialValues?.data),
  );
  const set = (patch: Partial<EditChaseForm>) =>
    setValue((s) => ({ ...s, ...patch }));

  const updateChase = async () => {
    const problem = validateEditForm(value);
    if (problem) return toast.error(problem);

    setLoading(true);
    try {
      await axiosInstance.patch(
        `/chase/${initialValues?.id}`,
        toChasePayload(value),
      );
      UpdateItemData();
      toast.success("Chase updated successfully");
      onClose();
    } catch (error) {
      toast.error(ParseErrors(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalPortal>
      <div
        className="z-[99999] fixed inset-0 bg-black/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-6 sm:py-10 px-3 sm:px-5 animate-fade-in"
        // An upload in flight isn't dismissed by a stray click on the backdrop.
        onClick={() => !loading && onClose()}
      >
        <div
          className="bg-surface border border-edge w-full max-w-[900px] rounded-2xl shadow-card"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-4 py-4 px-6 border-b border-edge bg-surface rounded-t-2xl">
            <div>
              <h2 className="font-heading-font text-[22px] sm:text-[25px] text-text-primary">
                Update Chase
              </h2>
              <p className="text-light-gray text-[13px]">
                {value.policyReferenceNumber || "—"} · quotes are not edited
                here.
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="h-9 w-9 shrink-0 flex items-center justify-center rounded-lg border border-edge bg-overlay text-text-primary hover:border-indigo-400/40 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className="p-5 sm:p-6">
            {/* ------------------------- CUSTOMER & POLICY ------------------------- */}
            <Section
              icon={faUser}
              title="Customer & Policy"
              desc="Who the policy holder is and the core policy references."
            >
              <Grid>
                <CustomInput
                  label="Fore Name *"
                  value={value.forename}
                  onChange={(e) => set({ forename: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Surname *"
                  value={value.surname}
                  onChange={(e) => set({ surname: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Company Name"
                  value={value.companyName}
                  onChange={(e) => set({ companyName: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Email *"
                  value={value.email}
                  onChange={(e) => set({ email: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Phone *"
                  value={value.telephone}
                  onChange={(e) => set({ telephone: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Date of Birth"
                  type="date"
                  value={value.dateOfBirth}
                  onChange={(e) => set({ dateOfBirth: e.target.value })}
                />
                <CustomInput
                  label="Post Code"
                  value={value.postcode}
                  onChange={(e) => set({ postcode: e.target.value })}
                  className="uppercase"
                  autoComplete="off"
                />
                <CustomInput
                  label="Address 1"
                  value={value.address}
                  onChange={(e) => set({ address: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Policy Reference Number *"
                  value={value.policyReferenceNumber}
                  onChange={(e) =>
                    set({ policyReferenceNumber: e.target.value })
                  }
                  // Only a renewed policy gets its live policy ref here.
                  disabled={!value.renewed}
                  autoComplete="off"
                />
                <CustomInput
                  label="Version Reference Number *"
                  value={value.versionReferenceNumber}
                  disabled
                  autoComplete="off"
                />
                <CustomSelect
                  comboboxType="select"
                  label="Brand *"
                  labelProperty="label"
                  valueProperty="value"
                  options={BRAND_OPTIONS}
                  value={BRAND_OPTIONS.find((o) => o.value === value.brandCode)}
                  onSingleSelectChange={(o) =>
                    set({
                      brandCode: (o as any)?.value ?? BRAND_OPTIONS[0].value,
                    })
                  }
                />
                <CustomSelect
                  comboboxType="select"
                  label="Policy Source"
                  placeholder="Select a source…"
                  labelProperty="label"
                  valueProperty="value"
                  options={POLICY_SOURCE_OPTIONS}
                  value={POLICY_SOURCE_OPTIONS.find(
                    (o) => o.value === value.policySource,
                  )}
                  onSingleSelectChange={(o) =>
                    set({ policySource: (o as any)?.value ?? "" })
                  }
                />
                <CustomSelect
                  comboboxType="select"
                  label="Cover Type"
                  labelProperty="label"
                  valueProperty="value"
                  options={COVER_TYPE_OPTIONS}
                  value={COVER_TYPE_OPTIONS.find(
                    (o) => o.value === value.coverType,
                  )}
                  onSingleSelectChange={(o) =>
                    set({
                      coverType:
                        (o as any)?.value ?? COVER_TYPE_OPTIONS[0].value,
                    })
                  }
                />
                <CustomInput
                  label="Policy Number"
                  value={value.policyNumber}
                  onChange={(e) => set({ policyNumber: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Insurer Name"
                  value={value.insurer}
                  onChange={(e) => set({ insurer: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Registration Number"
                  value={value.registrationNumber}
                  onChange={(e) => set({ registrationNumber: e.target.value })}
                  autoComplete="off"
                />
                <CustomInput
                  label="Renewal Date *"
                  type="date"
                  value={value.renewalDate}
                  onChange={(e) => set({ renewalDate: e.target.value })}
                  disabled={true}
                />
                <CustomInput
                  label="Start Date"
                  type="date"
                  value={value.startDate}
                  onChange={(e) => set({ startDate: e.target.value })}
                  helperText="Day 1 of the chase timeline."
                  disabled={true}
                />
              </Grid>
            </Section>

            {/* -------------------------- POLICY OPTIONS -------------------------- */}
            <Section
              icon={faSlidersH}
              title="Policy Options"
              desc="Risk, payment and renewal behaviour for this chase."
            >
              {/* Read-only once the chase exists — risk, payment and auto-renew
                  are what the timeline and the templates were built from. */}
              <fieldset
                disabled
                className="grid grid-cols-1 sm:grid-cols-3 gap-5 opacity-60 pointer-events-none"
              >
                <ChoiceGroup label="Risk Type">
                  <CustomRadioButton
                    id="edit-rk-std"
                    name="policyType"
                    label="Standard"
                    value="Standard"
                    checked={value.policyType === "Standard"}
                    onChange={() => set({ policyType: "Standard" })}
                  />
                  <CustomRadioButton
                    id="edit-rk-non"
                    name="policyType"
                    label="Non-Standard"
                    value="Non-Standard"
                    checked={value.policyType === "Non-Standard"}
                    onChange={() => set({ policyType: "Non-Standard" })}
                  />
                </ChoiceGroup>
                <ChoiceGroup label="Payment Type">
                  <CustomRadioButton
                    id="edit-pt-cash"
                    name="paymentType"
                    label="Cash"
                    value="cash"
                    checked={value.paymentType === "cash"}
                    onChange={() => set({ paymentType: "cash" })}
                  />
                  <CustomRadioButton
                    id="edit-pt-inst"
                    name="paymentType"
                    label="Instalments"
                    value="instalments"
                    checked={value.paymentType === "instalments"}
                    onChange={() => set({ paymentType: "instalments" })}
                  />
                </ChoiceGroup>
                <ChoiceGroup label="Auto Renew">
                  <CustomRadioButton
                    id="edit-ar-true"
                    name="auto"
                    label="True"
                    value="true"
                    checked={value.auto === true}
                    onChange={() => set({ auto: true })}
                  />
                  <CustomRadioButton
                    id="edit-ar-false"
                    name="auto"
                    label="False"
                    value="false"
                    checked={value.auto === false}
                    onChange={() => set({ auto: false })}
                  />
                </ChoiceGroup>
              </fieldset>

              {!value.auto && (
                <div className="mt-5">
                  <CustomSelect
                    comboboxType="select"
                    label="Lapse Reason (why isn't this policy auto-renewing?)"
                    placeholder="Select a reason…"
                    labelProperty="label"
                    valueProperty="value"
                    showTop
                    options={LAPSE_REASONS}
                    value={LAPSE_REASONS.find(
                      (o) => o.value === value.lapseReason,
                    )}
                    onSingleSelectChange={(o) =>
                      set({ lapseReason: (o as any)?.value ?? "" })
                    }
                  />
                </div>
              )}
            </Section>

            {/* ------------------------------ PRICING ------------------------------ */}
            <Section
              icon={faSterlingSign}
              title="Pricing"
              desc="The figures held on the chase row itself — view only."
            >
              <Grid>
                <CustomInput
                  label="Premium (£)"
                  type="number"
                  allowDecimal
                  value={value.premium}
                  disabled
                />
                {value.paymentType === "instalments" && (
                  <>
                    <CustomInput
                      label="Instalments"
                      type="number"
                      value={value.instalments}
                      disabled
                    />
                    <CustomInput
                      label="Interest Rate (%)"
                      type="number"
                      allowDecimal
                      value={value.interestRate}
                      disabled
                    />
                    <CustomInput
                      label="Monthly Amount (£)"
                      type="number"
                      allowDecimal
                      value={value.monthlyAmount}
                      disabled
                    />
                    <CustomInput
                      label="Total Payable (£)"
                      type="number"
                      allowDecimal
                      value={value.totalPayable}
                      disabled
                    />
                  </>
                )}
              </Grid>
              {value.paymentType === "cash" && (
                <p className="text-light-gray text-[13px] mt-4">
                  Cash policies carry no monthly figures — instalments, interest
                  rate, monthly amount and total payable are cleared on save.
                </p>
              )}
            </Section>

            {/* ------------------------ RENEWAL DOCUMENTS ------------------------ */}
            <Section
              icon={faFolderOpen}
              title="Renewal Documents"
              desc="What's on the pack already. New documents are added when marking as renewed."
            >
              <RenewalDocuments
                stored={initialValues?.data?.renewalDocuments ?? []}
              />
            </Section>
          </div>

          <div className="px-5 pb-5">
            <CustomButton
              className="uppercase"
              variant="primary"
              fullWidth
              disabled={loading}
              onClick={updateChase}
            >
              {loading ? <Loader /> : "Update Chase"}
            </CustomButton>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};

const Section: React.FC<{
  icon: IconProp;
  title: string;
  desc?: string;
  children: React.ReactNode;
}> = ({ icon, title, desc, children }) => (
  <section className="rounded-2xl border border-edge bg-surface-muted/40 p-4 sm:p-5 mb-5">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-white bg-gradient-to-br from-accent to-accent-bright shadow-sm shadow-indigo-500/30">
        <FontAwesomeIcon icon={icon} />
      </div>
      <div>
        <h3 className="text-heading-color font-bold text-[17px] leading-tight">
          {title}
        </h3>
        {desc && <p className="text-light-gray text-[13px] mt-0.5">{desc}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
    {children}
  </div>
);

const ChoiceGroup: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div className="rounded-xl border border-edge bg-surface p-4">
    <label className="text-label-muted font-medium block text-sm mb-3">
      {label}
    </label>
    <div className="flex flex-wrap gap-4">{children}</div>
  </div>
);

export default EditChaseModel;
